import { useId, useRef, useState, type ChangeEvent } from 'react';
import { Alert, Avatar, Box, Button, CircularProgress, Stack, Typography } from '../primitives';
import * as Icons from '../icons';
import { palette } from '../theme/palette';
import { usePortalPalette } from '../theme/usePortalPalette';
import { iconSize } from '@munifocus/tokens';
import { initialsOf } from '@munifocus/utils';

/**
 * Types the picker offers and the field accepts.
 *
 * Kept in step with the server allowlist in `AvatarService`. The client copy is
 * a courtesy — it stops an obviously wrong file before it is uploaded — and is
 * never the thing being relied on: the server re-checks the declared type
 * against the file's own magic bytes.
 */
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

/** Matches `MAX_AVATAR_BYTES` on the server. */
const MAX_BYTES = 4 * 1024 * 1024;

export interface ProfilePhotoLabels {
  /** e.g. "Profile photo" */
  title: string;
  /** e.g. "A photo helps colleagues recognise you in messages and on files." */
  description?: string;
  upload: string;
  replace: string;
  remove: string;
  /** Shown when the chosen file is the wrong type. */
  invalidType: string;
  /** Shown when the chosen file is over the size cap. */
  tooLarge: string;
  /** Shown when the request itself failed. */
  failed: string;
}

export interface ProfilePhotoFieldProps {
  /** Display name, used for the initials fallback and the image's alt text. */
  name: string;
  /** Current photo, or null to show initials. */
  avatarUrl: string | null | undefined;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
  labels: ProfilePhotoLabels;
  /** Rendered size of the preview, in px. */
  size?: number;
  /**
   * Shape of the preview.
   *
   * `circular` for a face, `rounded` for a logo. A municipal crest cropped to
   * a circle loses the corners of the thing it is a picture of, and a coat of
   * arms in a circle reads as somebody's avatar.
   */
  shape?: 'circular' | 'rounded';
}

/**
 * Set, replace or clear the signed-in user's profile photo.
 *
 * One component for all three portals: the account behind it is the same
 * `app_user` row everywhere, so a per-portal implementation would be three
 * chances to disagree about what a valid photo is.
 *
 * The real `<input type="file">` is kept in the DOM and driven by the button
 * rather than being created on the fly — a detached input never fires `change`
 * in Safari, and keeping it mounted also means the label association survives.
 */
export function ProfilePhotoField({
  name,
  avatarUrl,
  onUpload,
  onRemove,
  labels,
  size = 72,
  shape = 'circular',
}: ProfilePhotoFieldProps) {
  const portal = usePortalPalette();
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch {
      setError(labels.failed);
    } finally {
      setBusy(false);
    }
  }

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Cleared immediately so picking the *same* file again still fires a
    // change event — otherwise a failed upload cannot be retried as-is.
    event.target.value = '';
    if (!file) return;

    if (!ACCEPTED.includes(file.type)) {
      setError(labels.invalidType);
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(labels.tooLarge);
      return;
    }
    void run(() => onUpload(file));
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Avatar
            src={avatarUrl ?? undefined}
            alt={name}
            variant={shape}
            sx={{
              width: size,
              height: size,
              // A logo is a picture of a thing rather than a crop of a face:
              // fit it whole inside the frame instead of filling and cropping.
              ...(shape === 'rounded' && { '& img': { objectFit: 'contain' } }),
              fontSize: size / 2.8,
              border: `1px solid ${palette.border.line}`,
              backgroundColor: palette.background.default,
              color: palette.text.secondary,
            }}
          >
            {initialsOf(name)}
          </Avatar>
          {busy && (
            <CircularProgress
              size={size + 8}
              thickness={2}
              sx={{ position: 'absolute', top: -4, left: -4, color: portal.brand.accent }}
            />
          )}
        </Box>

        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{labels.title}</Typography>
          {labels.description && (
            <Typography sx={{ fontSize: 12, color: palette.text.muted }}>
              {labels.description}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ pt: 0.5, flexWrap: 'wrap', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              startIcon={<Icons.Upload size={iconSize.md} />}
              onClick={() => inputRef.current?.click()}
            >
              {avatarUrl ? labels.replace : labels.upload}
            </Button>
            {avatarUrl && (
              <Button
                size="small"
                variant="text"
                disabled={busy}
                startIcon={<Icons.Trash2 size={iconSize.md} />}
                onClick={() => void run(onRemove)}
              >
                {labels.remove}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>

      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        onChange={onPick}
        // Visually hidden rather than display:none, which would make it
        // unreachable by the button's click(). It is hidden from assistive
        // tech and the tab order on purpose: the labelled button beside it is
        // the control, and a second unnamed one would only be noise.
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        tabIndex={-1}
        aria-hidden="true"
      />

      {error && (
        <Alert severity="error" sx={{ mt: 1.5, fontSize: 13 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
